import { AgentState } from '@livekit/components-react'
import React, { useRef, useState, useEffect } from 'react'

interface VoiceVisualizationProps {
  size?: number
  audioLevel?: number
  state?: AgentState
  primaryColor: string
  secondaryColor: string
}

const VoiceVisualization: React.FC<VoiceVisualizationProps> = ({
  size = 200,
  audioLevel = 0,
  state = 'connecting',
  primaryColor = '#4169E1', // Default royal blue
  secondaryColor = '#8A2BE2' // Default violet
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>(0)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const frameInterval = 1000 / 60 // 60 FPS for smoother animation

  // Animation parameters
  const particlesRef = useRef<
    Array<{
      x: number
      y: number
      radius: number
      color: string
      velocity: { x: number; y: number }
      alpha: number
      growth: number
      // Add 3D-like properties
      z: number // z-position for pseudo-3D
      rotationSpeed: number
      phase: number // For wave-like motion
    }>
  >([])

  // Additional array for center micro-spheres
  const centerSpheresRef = useRef<
    Array<{
      x: number
      y: number
      z: number
      originalX: number
      originalY: number
      originalZ: number
      radius: number
      color: string
      alpha: number
      phase: number
      velocity: { x: number; y: number; z: number }
      scatter: boolean
      returnSpeed: number
    }>
  >([])

  const timeRef = useRef<number>(0)
  const rotationRef = useRef<number>(0)
  const colorPaletteRef = useRef<string[]>([])
  const lastAudioLevelRef = useRef<number>(0)
  const audioHistoryRef = useRef<number[]>([])
  const isScatteredRef = useRef<boolean>(false)
  const audioThresholdRef = useRef<number>(0.1) // Threshold for scattering

  // Generate smoother color palette based on primary and secondary colors
  const generateColorPalette = (): void => {
    try {
      // Parse the colors to ensure they're valid
      const primary = primaryColor.trim()
      const secondary = secondaryColor.trim()

      // Create a gradient of colors between primary and secondary
      const palette: string[] = []

      // Create more intermediate colors (30 instead of 20) for even smoother transitions
      const steps = 30
      for (let i = 0; i <= steps; i++) {
        const ratio = i / steps
        const r = Math.round(
          parseInt(secondary.slice(1, 3), 16) * (1 - ratio) +
            parseInt(primary.slice(1, 3), 16) * ratio
        )
        const g = Math.round(
          parseInt(secondary.slice(3, 5), 16) * (1 - ratio) +
            parseInt(primary.slice(3, 5), 16) * ratio
        )
        const b = Math.round(
          parseInt(secondary.slice(5, 7), 16) * (1 - ratio) +
            parseInt(primary.slice(5, 7), 16) * ratio
        )

        palette.push(
          `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
        )
      }

      colorPaletteRef.current = palette
    } catch (error) {
      console.error('Error generating color palette:', error)
      // Fallback palette
      colorPaletteRef.current = [primaryColor, secondaryColor]
    }
  }

  // Initialize canvas with enhanced 3D properties
  useEffect(() => {
    if (!isInitialized) {
      const canvas = canvasRef.current
      if (!canvas) return

      // Set fixed dimensions with device pixel ratio for high-DPI displays
      const pixelRatio = Math.min(window.devicePixelRatio, 2)
      canvas.width = size * pixelRatio
      canvas.height = size * pixelRatio

      canvas.style.width = `${size}px`
      canvas.style.height = `${size}px`

      // Generate initial particles with 3D properties
      const centerX = canvas.width / 2
      const centerY = canvas.height / 2
      const particles = []

      // Initialize with more particles and better distribution
      for (let i = 0; i < 200; i++) {
        // Use spherical coordinates for better particle distribution
        const phi = Math.random() * Math.PI * 2 // Azimuthal angle
        const theta = Math.random() * Math.PI // Polar angle
        const distance = ((Math.random() * size) / 2.5) * pixelRatio // Increased to fill more space

        // Convert to cartesian coordinates for better particle distribution
        const x = centerX + distance * Math.sin(theta) * Math.cos(phi)
        const y = centerY + distance * Math.sin(theta) * Math.sin(phi)
        const z = distance * Math.cos(theta) * 0.5 // Scaled z for flatter appearance

        // Make initial particle size smaller, especially near the center
        const distanceFactor = distance / ((size / 2.5) * pixelRatio) // Normalize distance to 0-1 range
        const particleRadius = (Math.random() * 3 + 1) * pixelRatio * (0.5 + distanceFactor * 0.5)

        particles.push({
          x: x,
          y: y,
          z: z - 25, // Adjust z-position to center around 0
          radius: particleRadius, // Smaller particles
          color: '#ffffff',
          velocity: {
            x: (Math.random() - 0.5) * 0.3,
            y: (Math.random() - 0.5) * 0.3
          },
          alpha: Math.random() * 0.7 + 0.3,
          growth: Math.random() * 0.05 - 0.025, // Reduced growth rate
          rotationSpeed: (Math.random() - 0.5) * 0.002,
          phase: Math.random() * Math.PI * 2
        })
      }

      particlesRef.current = particles

      // Initialize center micro-spheres with more density
      const centerSpheres = []
      const centerRadius = (size / 4) * pixelRatio // Increased radius for center sphere area

      // Create more tiny spheres in the center for a denser effect
      for (let i = 0; i < 100; i++) {
        // Use spherical coordinates for better distribution
        const phi = Math.random() * Math.PI * 2 // Azimuthal angle
        const theta = Math.random() * Math.PI // Polar angle
        const radius = Math.random() * centerRadius * 0.8 // Random radius within center area

        // Convert to cartesian coordinates
        const x = centerX + radius * Math.sin(theta) * Math.cos(phi)
        const y = centerY + radius * Math.sin(theta) * Math.sin(phi)
        const z = radius * Math.cos(theta) * 0.5 // Scaled z for flatter appearance

        centerSpheres.push({
          x: x,
          y: y,
          z: z,
          originalX: x, // Store original position for return movement
          originalY: y,
          originalZ: z,
          radius: (Math.random() * 1.5 + 0.5) * pixelRatio, // Very small sphere radius
          color: '#ffffff',
          alpha: Math.random() * 0.5 + 0.3,
          phase: Math.random() * Math.PI * 2,
          velocity: {
            x: 0,
            y: 0,
            z: 0
          },
          scatter: false,
          returnSpeed: Math.random() * 0.03 + 0.02 // Different return speeds for variation
        })
      }

      centerSpheresRef.current = centerSpheres

      // Generate color palette
      generateColorPalette()

      // Initialize audio history buffer for smoother transitions
      audioHistoryRef.current = Array(10).fill(0)

      setIsInitialized(true)
    }
  }, [size, isInitialized])

  // Update color palette when colors change
  useEffect(() => {
    generateColorPalette()
  }, [primaryColor, secondaryColor])

  // Main animation loop
  useEffect(() => {
    if (!isInitialized) return

    // Start animation with current timestamp
    timeRef.current = performance.now()
    animationRef.current = requestAnimationFrame(animate)

    // Update audio history with new level
    const audioHistory = audioHistoryRef.current
    audioHistory.push(audioLevel)
    if (audioHistory.length > 10) audioHistory.shift()

    // Smooth audio level transitions
    const smoothedAudio = audioHistory.reduce((sum, level) => sum + level, 0) / audioHistory.length
    lastAudioLevelRef.current = smoothedAudio

    // Check if we should scatter the spheres
    const shouldScatter = smoothedAudio > audioThresholdRef.current

    // If state changed, update scatter state for all spheres
    if (shouldScatter !== isScatteredRef.current) {
      isScatteredRef.current = shouldScatter

      // Apply scatter behavior to all center spheres
      const centerSpheres = centerSpheresRef.current
      for (let i = 0; i < centerSpheres.length; i++) {
        const sphere = centerSpheres[i]
        if (sphere) {
          if (shouldScatter && !sphere?.scatter) {
            // Scatter in random direction with speed based on audio level
            // Increased speed multiplier from 5 to 10 for farther scattering
            const speed = 1 + smoothedAudio * 10
            const angle = Math.random() * Math.PI * 2
            const elevation = Math.random() * Math.PI - Math.PI / 2

            sphere.velocity.x = Math.cos(angle) * Math.cos(elevation) * speed
            sphere.velocity.y = Math.sin(angle) * Math.cos(elevation) * speed
            sphere.velocity.z = Math.sin(elevation) * speed
            sphere.scatter = true
          } else if (!shouldScatter) {
            // Start returning to original position
            sphere.scatter = false
          }
        }
      }
    }

    return (): void => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isInitialized, audioLevel])

  // Enhanced animation loop with 3D-like effects
  const animate = (timestamp: number): void => {
    const canvas = canvasRef.current
    if (!canvas) {
      animationRef.current = requestAnimationFrame(animate)
      return
    }

    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) {
      animationRef.current = requestAnimationFrame(animate)
      return
    }

    // Limit frame rate
    if (timestamp - timeRef.current >= frameInterval) {
      timeRef.current = timestamp

      // Clear canvas with fade effect for smoother trails
      ctx.fillStyle = 'transparent' // Use transparent for fade effect

      // Smoothly transition audio level for more organic movement

      const targetAudioLevel = Math.min(1, Math.max(0, Math.abs(audioLevel) * 2.5))
      let currentAudioLevel = lastAudioLevelRef.current
      currentAudioLevel += (targetAudioLevel - currentAudioLevel) * 0.1
      lastAudioLevelRef.current = currentAudioLevel

      // Enhanced normalized audio with smoother transition
      const normalizedAudio = currentAudioLevel

      // Calculate center of canvas
      const centerX = canvas.width / 2
      const centerY = canvas.height / 2

      // Slowly rotate the entire visualization for 3D effect
      rotationRef.current += 0.001

      // Draw enhanced base sphere glow with depth effect - seamless color blend
      const baseRadius = canvas.width * 0.27 * (1 + normalizedAudio * 0.15)

      // Create a more seamless gradient using our enhanced color palette
      const gradient = ctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        baseRadius * 2.0 // Increased to fill more space
      )

      // Use more color stops from the palette for a smoother transition
      const colorPalette = colorPaletteRef.current

      // Use more colors from the palette to create a smoother blend without gaps
      for (let i = 0; i < colorPalette.length; i++) {
        const stop = i / (colorPalette.length - 1)
        // Increased alpha values to enhance brightness and reduce gaps
        const alpha =
          i < colorPalette.length / 2
            ? Math.max(0.4, 1 - stop * 0.6)
            : Math.max(0.1, 0.8 - (stop - 0.5) * 1.2)

        gradient.addColorStop(
          stop,
          colorPalette[i] +
            Math.round(alpha * 255)
              .toString(16)
              .padStart(2, '0')
        )
      }

      ctx.fillStyle = gradient
      ctx.beginPath()
      ctx.arc(centerX, centerY, baseRadius * 2.0, 0, Math.PI * 2)
      ctx.fill()

      // Draw additional intermediate layer to help fill the gap
      const midGradient = ctx.createRadialGradient(
        centerX,
        centerY,
        baseRadius * 0.5,
        centerX,
        centerY,
        baseRadius * 1.2
      )

      for (let i = 0; i < colorPalette.length; i++) {
        const stop = i / (colorPalette.length - 1)
        // Higher alpha for this intermediate layer
        const alpha = Math.max(0.3, 0.8 - stop * 0.7)

        midGradient.addColorStop(
          stop,
          colorPalette[i] +
            Math.round(alpha * 255)
              .toString(16)
              .padStart(2, '0')
        )
      }

      ctx.fillStyle = midGradient
      // ctx.beginPath()
      // ctx.arc(centerX, centerY, baseRadius * 1.2, 0, Math.PI * 2)
      // ctx.fill()

      // Draw the center micro-spheres instead of one large sphere
      ctx.globalCompositeOperation = 'lighter'

      // Sort center spheres by z-index for proper 3D rendering
      const centerSpheres = centerSpheresRef.current
      centerSpheres.sort((a, b) => a.z - b.z)

      // Draw each small sphere in the center
      if (state === 'speaking') {
        for (let i = 0; i < centerSpheres.length; i++) {
          const sphere = centerSpheres[i]
          if (sphere) {
            // Update sphere position based on scatter state
            if (sphere.scatter) {
              // In scatter mode - move away from center with velocity
              sphere.x += sphere.velocity.x
              sphere.y += sphere.velocity.y
              sphere.z += sphere.velocity.z

              // Reduced drag from 0.98 to 0.99 to allow particles to travel farther
              sphere.velocity.x *= 0.99
              sphere.velocity.y *= 0.99
              sphere.velocity.z *= 0.99

              // Boundary check - if sphere goes too far, redirect it
              // Increased boundary distance by using baseRadius * 3.0 instead of 2.0
              const dx = sphere.x - centerX
              const dy = sphere.y - centerY
              const distance = Math.sqrt(dx * dx + dy * dy)

              // Increased maximum distance and z-range
              if (distance > baseRadius * 3.0 || Math.abs(sphere.z) > 150) {
                // Reduce redirection strength to allow more outward movement
                sphere.velocity.x -= dx * 0.008
                sphere.velocity.y -= dy * 0.008
                sphere.velocity.z -= sphere.z * 0.008
              }
            } else {
              // Return to original position
              const dx = sphere.originalX - sphere.x
              const dy = sphere.originalY - sphere.y
              const dz = sphere.originalZ - sphere.z

              // Move towards original position with return speed
              sphere.x += dx * sphere.returnSpeed
              sphere.y += dy * sphere.returnSpeed
              sphere.z += dz * sphere.returnSpeed
            }
          }

          // Pulsate with audio and time
          const time = timestamp * 0.001
          if (sphere) {
            const pulseFactor = 1 + Math.sin(time * 0.5 + sphere.phase) * 0.2
            const audioPulse = 1 + normalizedAudio * 0.3

            // Adjust position slightly based on audio
            const moveAmount = normalizedAudio * 5
            const offsetX = Math.cos(time + sphere.phase) * moveAmount
            const offsetY = Math.sin(time + sphere.phase) * moveAmount

            // Get a color from the palette
            const colorIndex = Math.floor(i % colorPalette.length)

            sphere.color = colorPalette[colorIndex] ?? 'transparent'

            // Calculate z-based scaling for depth effect
            const zScale = (50 - Math.abs(sphere.z)) / 50

            // Draw sphere with enhanced glow
            const sphereRadius = sphere.radius * pulseFactor * audioPulse * zScale
            const glow = ctx.createRadialGradient(
              sphere.x + offsetX,
              sphere.y + offsetY,
              0,
              sphere.x + offsetX,
              sphere.y + offsetY,
              Math.max(0, sphereRadius * 4)
            )

            // Create smoother, brighter glow
            glow.addColorStop(0, sphere.color + 'ff')
            glow.addColorStop(0.4, sphere.color + 'a0') // Slower falloff
            glow.addColorStop(1, sphere.color + '00')

            ctx.globalAlpha = sphere.alpha * zScale
            ctx.fillStyle = glow
            ctx.beginPath()
            ctx.arc(
              sphere.x + offsetX,
              sphere.y + offsetY,
              Math.max(0.0001, sphereRadius * 4),
              0,
              Math.PI * 2
            )
            ctx.fill()

            // Draw sphere core
            ctx.fillStyle = 'rgba(255, 255, 255, ' + sphere.alpha * zScale + ')'
            ctx.beginPath()
            ctx.arc(
              sphere.x + offsetX,
              sphere.y + offsetY,
              Math.max(0.0001, sphereRadius),
              0,
              Math.PI * 2
            )
            ctx.fill()
          }
        }
      }

      // Draw enhanced particles with depth sorting for 3D effect
      const particles = particlesRef.current

      // Sort particles by z-index for proper 3D rendering
      particles.sort((a, b) => a.z - b.z)
      if (state === 'speaking') {
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i]

          // Calculate distance from center
          if (!p) continue
          const dx = p.x - centerX
          const dy = p.y - centerY
          const distance = Math.sqrt(dx * dx + dy * dy)

          // Apply 3D rotation to particles

          // Update z position with wave-like motion
          p.z += Math.sin(timestamp * 0.001 + p.phase) * 0.2

          // Constrain z to prevent particles from going too far
          if (p.z > 50) p.z = 50
          if (p.z < -50) p.z = -50

          // Scale based on z position for perspective effect
          const zScale = (50 - p.z) / 100 + 0.5

          // Audio-based adjustments - Increased from 2 to 3 for more audio influence
          const audioInfluence = normalizedAudio * 3

          // Update particle position with 3D wobble effect
          const time = timestamp / 1000
          const wobbleX = Math.sin(time + i + p.phase) * 2 * audioInfluence
          const wobbleY = Math.cos(time + i + p.phase) * 2 * audioInfluence

          p.x += (p.velocity.x + wobbleX * 0.05) * zScale
          p.y += (p.velocity.y + wobbleY * 0.05) * zScale

          // Gravitational effect with 3D influence - reduced from 0.015 to 0.012
          const gravityFactor = 0.012 * zScale
          const angle = Math.atan2(dy, dx)
          p.velocity.x -= Math.cos(angle) * gravityFactor
          p.velocity.y -= Math.sin(angle) * gravityFactor

          // Apply audio-based velocity boost - increased from 0.03 to 0.05
          p.velocity.x *= 1 + audioInfluence * 0.05
          p.velocity.y *= 1 + audioInfluence * 0.05

          // Increased max velocity from 1.2 to 1.5
          const maxVelocity = 1.5
          const velocityMagnitude = Math.sqrt(
            p.velocity.x * p.velocity.x + p.velocity.y * p.velocity.y
          )
          if (velocityMagnitude > maxVelocity) {
            p.velocity.x = (p.velocity.x / velocityMagnitude) * maxVelocity
            p.velocity.y = (p.velocity.y / velocityMagnitude) * maxVelocity
          }

          // Reset particles that go too far from center with new z position
          // Increased from 2.2 to 3.0 to allow particles to travel farther
          if (distance > baseRadius * 3.0) {
            const newAngle = Math.random() * Math.PI * 2
            const newDistance = Math.random() * baseRadius * 0.8
            p.x = centerX + Math.cos(newAngle) * newDistance
            p.y = centerY + Math.sin(newAngle) * newDistance
            p.z = Math.random() * 50 - 25
            p.velocity.x = (Math.random() - 0.5) * 0.3
            p.velocity.y = (Math.random() - 0.5) * 0.3
            p.phase = Math.random() * Math.PI * 2
          }

          // Gradually change particle radius for pulsating effect
          p.radius += p.growth
          if (p.radius > 7 || p.radius < 1) {
            p.growth = -p.growth
          }

          // Reduce particle size based on proximity to center
          const centerProximity = distance / baseRadius
          const sizeMultiplier = Math.min(1, 0.5 + centerProximity * 0.8) // Particles near center are smaller

          // Audio-based size adjustment with z-scaling
          const sizeBoost = 1 + normalizedAudio * 0.5
          const displayRadius = p.radius * sizeBoost * zScale * sizeMultiplier

          // Select color from palette based on distance and z-position
          // Updated to use a larger distance denominator (3.0 instead of 2.2)
          const colorIndex = Math.min(
            colorPaletteRef.current.length - 1,
            Math.floor(
              ((distance / (baseRadius * 3.0) + (p.z + 50) / 100) *
                colorPaletteRef.current.length) /
                2
            )
          )

          p.color = colorPaletteRef.current[colorIndex] ?? 'transparent'

          // Adjust alpha based on z position for depth effect
          const zAlpha = (50 - Math.abs(p.z)) / 50

          // Draw particle with enhanced glow and depth effect
          const safeRadius = Math.max(0, displayRadius * 3 * zScale)
          const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, safeRadius)

          glow.addColorStop(0, p.color + 'ff')
          glow.addColorStop(0.5, p.color + '90') // More visible glow
          glow.addColorStop(1, p.color + '00')

          ctx.fillStyle = glow
          ctx.globalAlpha = p.alpha * zAlpha
          ctx.beginPath()
          ctx.arc(p.x, p.y, Math.max(0.0001, displayRadius * 3 * zScale), 0, Math.PI * 2)
          ctx.fill()

          // Draw particle core with depth effect
          ctx.fillStyle = 'rgba(255, 255, 255, ' + p.alpha * zAlpha + ')'
          ctx.beginPath()
          ctx.arc(p.x, p.y, Math.max(0.0001, displayRadius * zScale), 0, Math.PI * 2)
          ctx.fill()

          ctx.globalAlpha = 1.0
        }
      }

      // Reset composite operation
      ctx.globalCompositeOperation = 'source-over'
    }

    // Schedule next frame
    animationRef.current = requestAnimationFrame(animate)
  }

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'block'
      }}
    />
  )
}

export default VoiceVisualization
